import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { X, Sparkle, PaperPlaneTilt, ArrowsClockwise, FileText, CheckCircle, Warning, ShieldCheck, ShieldWarning, Shield } from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * PrepareEAModal
 * ----------------
 * Big review-and-send modal for the institution portal. Opens when staff
 * clicks "Prepare EA" on a student card. Fetches a pre-filled draft from
 * `/api/institution/students/:id/ea/prepare` (which includes AI-detected
 * ID + English proof), lets staff edit every field, and POSTs the reviewed
 * payload to `/api/institution/students/:id/ea/send` to fire the DocuSign
 * envelope.
 *
 * Design notes:
 *  - Fields grouped into semantic sections (Student, Program, Admission
 *    Evidence, Fees). Left rail acts as a mini-TOC as staff scrolls.
 *  - AI-detected fields carry a small "detected from: filename.pdf" hint
 *    so staff can trust or override at a glance.
 *  - Re-run AI is a single button (force_refresh=true) — cheap safety net.
 */
export default function PrepareEAModal({ open, onOpenChange, student: studentProp, onSent }) {
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [draft, setDraft] = useState(null);
  const [error, setError] = useState('');
  const [previewing, setPreviewing] = useState(false);

  // Cache the student on modal open. If the parent drawer closes (Radix Sheet
  // loses focus when the Preview PDF tab opens), `studentProp` briefly becomes
  // null. Without this ref we'd lose the whole context mid-flow and dump a
  // "No student loaded" red banner in front of the user.
  const studentRef = useRef(null);
  if (open && studentProp?.id && studentRef.current?.id !== studentProp.id) {
    studentRef.current = studentProp;
  }
  const student = studentProp?.id ? studentProp : studentRef.current;

  const handlePreview = async () => {
    if (!draft || previewing) return;
    // Open the tab SYNCHRONOUSLY inside the click gesture so Chrome/Safari
    // don't popup-block the eventual navigation. We fill it with a "loading"
    // placeholder now, then swap to the PDF blob URL when it's ready.
    const win = window.open('', '_blank');
    if (win && win.document) {
      win.document.title = 'Preparing EA preview…';
      win.document.body.style.cssText = 'font-family:-apple-system,sans-serif;padding:48px;color:#555;';
      win.document.body.innerHTML = '<p>Rendering preview…</p>';
    }
    setPreviewing(true);
    try {
      const resp = await axios.post(
        `${API}/institution/students/${student.id}/ea/preview.pdf`,
        draft,
        { responseType: 'blob', timeout: 30000 },
      );
      const url = URL.createObjectURL(new Blob([resp.data], { type: 'application/pdf' }));
      if (win && !win.closed) {
        win.location.href = url;
      } else {
        // Popup was blocked — offer a download instead.
        const a = document.createElement('a');
        a.href = url;
        a.download = `ea-preview-${student.id}.pdf`;
        a.click();
      }
    } catch (e) {
      if (win && !win.closed) win.close();
      toast.error(e?.response?.data?.detail || e?.message || 'Preview failed');
    } finally {
      setPreviewing(false);
    }
  };

  const load = async (forceRefresh = false, campus = null) => {
    if (!student?.id) {
      setError('No student loaded — please re-open this from the student card.');
      setLoading(false);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const { data } = await axios.post(`${API}/institution/students/${student.id}/ea/prepare`, {
        force_refresh: forceRefresh,
        campus,
      });
      setDraft(data.draft);
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || 'Failed to prepare EA.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!open) {
      // Reset internal state when the modal fully closes so the next open
      // gets a fresh load and doesn't flash stale data.
      studentRef.current = null;
      setDraft(null);
      setError('');
      return;
    }
    // Only (re)load when we have a real student id. If the parent
    // momentarily re-renders with a null studentProp (Radix Sheet portal
    // shuffling on focus/blur), we keep whatever draft is already on
    // screen instead of blanking it out — that was the "brief red flash"
    // users saw while typing.
    if (!studentProp?.id) return;
    setDraft(null);
    load(false, null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, studentProp?.id]);

  const set = (key, value) => setDraft((d) => ({ ...d, [key]: value }));

  const handleSend = async () => {
    if (!draft) return;
    // If AI compliance is red, force the user through the override dialog
    // BEFORE hitting the server. Cleaner UX than watching the 422 bounce.
    const overall = (draft.compliance?.overall || '').toLowerCase();
    if (overall === 'red' && !draft.compliance_override_reason) {
      const reason = window.prompt(
        'AI compliance audit flagged this student as RED.\n\n' +
        'You are about to send the Enrollment Agreement anyway. This override will be logged.\n\n' +
        'Please describe why you are proceeding (min 10 chars):'
      );
      if (!reason || reason.trim().length < 10) {
        toast.error('Override cancelled — reason must be at least 10 characters.');
        return;
      }
      setDraft((d) => ({ ...d, compliance_override_reason: reason.trim() }));
      // Continue to send with the override attached.
      draft.compliance_override_reason = reason.trim();
    }
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/institution/students/${student.id}/ea/send`, draft);
      toast.success(`Enrollment agreement sent — DocuSign envelope ${data.envelope_id?.slice(0, 8)}…`);
      onSent?.(data);
      onOpenChange(false);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail?.code === 'compliance_red_blocked') {
        toast.error('Server rejected send: compliance red flag. Please supply an override reason.');
      } else {
        toast.error(detail || detail?.message || 'Send failed. Check DocuSign template config.');
      }
    } finally {
      setSending(false);
    }
  };

  if (!open) return null;

  const modalNode = (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm p-3"
      style={{ pointerEvents: 'auto' }}
      role="dialog"
      aria-modal="true"
      data-testid="prepare-ea-modal"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onOpenChange(false); }}
    >
      <div
        className="w-full max-w-4xl bg-white shadow-2xl flex flex-col rounded-lg overflow-hidden"
        style={{ maxHeight: 'min(92vh, calc(100vh - 24px))', pointerEvents: 'auto' }}
        onMouseDown={(e) => e.stopPropagation()}
        onWheel={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div className="min-w-0">
            <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.22em] text-gray-500">
              <Sparkle size={11} weight="fill" className="text-emerald-600" />
              Prepare Enrollment Agreement
            </div>
            <h2 className="mt-1 text-xl font-semibold text-gray-900 truncate" style={{ fontFamily: "'Libre Baskerville', serif" }} data-testid="prepare-ea-title">
              {student?.full_name || 'Student'}
            </h2>
          </div>
          <button
            onClick={() => onOpenChange(false)}
            className="shrink-0 w-9 h-9 rounded-md flex items-center justify-center hover:bg-gray-100 text-gray-500"
            aria-label="Close"
            data-testid="prepare-ea-close"
          >
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 min-h-0 overflow-y-auto" data-testid="prepare-ea-body">
          {loading && (
            <div className="p-12 text-center text-sm text-gray-500">
              <div className="inline-flex items-center gap-2">
                <ArrowsClockwise size={16} className="animate-spin" />
                Preparing draft &amp; analysing uploaded documents…
              </div>
            </div>
          )}

          {error && !loading && (
            <div className="p-6 text-sm text-red-700 bg-red-50 border-y border-red-200 flex items-center gap-2">
              <Warning size={16} weight="fill" /> {error}
            </div>
          )}

          {draft && !loading && (
            <div className="p-6 space-y-8">
              {/* AI classifier banner */}
              {draft.ai_classifier && (
                <div className="flex items-start gap-2 rounded-md bg-emerald-50 border border-emerald-200 px-3 py-2 text-[12px] text-emerald-800">
                  <Sparkle size={14} weight="fill" className="mt-0.5 text-emerald-600 shrink-0" />
                  <div className="min-w-0">
                    <p><strong>AI-detected fields</strong> — please verify Identification &amp; English Proof below. Classifier: <code className="text-[10px]">{draft.ai_classifier}</code></p>
                    {draft.ai_notes && <p className="mt-0.5 text-emerald-700/80 italic">{draft.ai_notes}</p>}
                  </div>
                  <button onClick={() => load(true, draft.campus)} className="ml-auto shrink-0 text-[11px] font-semibold text-emerald-700 hover:text-emerald-900 underline" data-testid="prepare-ea-rerun-ai">
                    Re-run AI
                  </button>
                </div>
              )}

              {/* Compliance audit banner — red/yellow/green readiness gate */}
              <ComplianceBanner compliance={draft.compliance} />

              <Section title="Student Info">
                <Row><Field label="Last Name" value={draft.last_name} onChange={(v) => set('last_name', v)} testid="ea-last-name" /></Row>
                <Row><Field label="First / Middle Name" value={draft.first_middle_name} onChange={(v) => set('first_middle_name', v)} testid="ea-first-middle" /></Row>
                <Row><Field label="Full Name (as it appears in EA)" value={draft.full_name} onChange={(v) => set('full_name', v)} testid="ea-full-name" /></Row>
                <Row>
                  <Field label="Personal Education #" value={draft.personal_education_number} onChange={(v) => set('personal_education_number', v)} testid="ea-pen" />
                  <div className="flex-1 min-w-0">
                    <Label className="text-[10px] font-bold uppercase tracking-[0.18em] text-gray-500">Date of Birth</Label>
                    <Input
                      value={draft.date_of_birth || ''}
                      onChange={(e) => set('date_of_birth', e.target.value)}
                      placeholder="YYYY-MM-DD"
                      className="mt-1 text-sm"
                      data-testid="ea-dob"
                    />
                    {draft.ai_dob_source && (
                      <p className="mt-1 text-[10px] text-emerald-700/80 flex items-center gap-1">
                        <FileText size={10} /> Detected from: {draft.ai_dob_source}
                      </p>
                    )}
                  </div>
                </Row>
                <Row>
                  <Field label="Gender" value={draft.gender} onChange={(v) => set('gender', v)} testid="ea-gender" />
                  <Field label="International (Yes/No)" value={draft.international} onChange={(v) => set('international', v)} testid="ea-intl" />
                </Row>
                <Row>
                  <Field label="Email" value={draft.email_address} onChange={(v) => set('email_address', v)} testid="ea-email" />
                  <Field label="Phone" value={draft.telephone_number} onChange={(v) => set('telephone_number', v)} testid="ea-phone" />
                </Row>
                <Row><Field label="Mailing Address" value={draft.canada_mailing_address} onChange={(v) => set('canada_mailing_address', v)} testid="ea-address" /></Row>
                <Row><Field label="Postal Code" value={draft.canada_postal_code} onChange={(v) => set('canada_postal_code', v)} testid="ea-postal" /></Row>
              </Section>

              <Section title="Program">
                <Row><Field label="Program Name" value={draft.program_name} onChange={(v) => set('program_name', v)} testid="ea-program" /></Row>
                <Row>
                  <Field label="Start Date" value={draft.program_start_date} onChange={(v) => set('program_start_date', v)} placeholder="YYYY-MM-DD" testid="ea-start" />
                  <Field label="End Date" value={draft.program_end_date} onChange={(v) => set('program_end_date', v)} placeholder="YYYY-MM-DD" testid="ea-end" />
                </Row>
                <Row>
                  <Field label="Credential" value={draft.credential} onChange={(v) => set('credential', v)} testid="ea-credential" />
                  <Field label="Delivery" value={draft.delivery} onChange={(v) => set('delivery', v)} testid="ea-delivery" />
                </Row>
                <Row>
                  <div className="flex-1">
                    <Label className="text-[10px] font-bold uppercase tracking-[0.18em] text-gray-500">Campus</Label>
                    <select
                      value={draft.campus || 'North Vancouver'}
                      onChange={(e) => set('campus', e.target.value)}
                      className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                      data-testid="ea-campus"
                    >
                      <option value="North Vancouver">North Vancouver</option>
                      <option value="Vernon">Vernon</option>
                    </select>
                  </div>
                </Row>
              </Section>

              <Section title="Admission Evidence" hint="AI-detected — click any field to override">
                <div>
                  <Label className="text-[10px] font-bold uppercase tracking-[0.18em] text-gray-500">Identification Documents</Label>
                  <Textarea
                    value={draft.identification_documents}
                    onChange={(e) => set('identification_documents', e.target.value)}
                    rows={2}
                    className="mt-1 text-sm"
                    placeholder="e.g. Canadian PR Card, Foreign Passport"
                    data-testid="ea-id-docs"
                  />
                  {(draft.ai_id_sources || []).length > 0 && (
                    <p className="mt-1 text-[10px] text-emerald-700/80 flex items-center gap-1">
                      <FileText size={10} /> Detected from: {(draft.ai_id_sources || []).join(', ')}
                    </p>
                  )}
                </div>
                <div>
                  <Label className="text-[10px] font-bold uppercase tracking-[0.18em] text-gray-500">Proof of English</Label>
                  <Textarea
                    value={draft.proof_of_english}
                    onChange={(e) => set('proof_of_english', e.target.value)}
                    rows={3}
                    className="mt-1 text-sm"
                    placeholder="e.g. IELTS 6.5 / Accuplacer / 3 years secondary…"
                    data-testid="ea-english-proof"
                  />
                  {draft.ai_english_source && (
                    <p className="mt-1 text-[10px] text-emerald-700/80 flex items-center gap-1">
                      <FileText size={10} /> Detected from: {draft.ai_english_source}
                    </p>
                  )}
                </div>
              </Section>

              <Section title="Fees & Payment Plan">
                <Row>
                  <Field label="Tuition Fee" value={draft.tuition_fee} onChange={(v) => set('tuition_fee', v)} testid="ea-tuition" />
                  <Field label="Application Fee" value={draft.application_fee} onChange={(v) => set('application_fee', v)} testid="ea-app-fee" />
                </Row>
                <Row>
                  <Field label="Program Cost" value={draft.program_cost} onChange={(v) => set('program_cost', v)} testid="ea-program-cost" />
                  <Field label="Total Program Cost" value={draft.total_program_cost} onChange={(v) => set('total_program_cost', v)} testid="ea-total" />
                </Row>
                <Row>
                  <Field label="Quarter 1" value={draft.quarter_1} onChange={(v) => set('quarter_1', v)} testid="ea-q1" />
                  <Field label="Quarter 2" value={draft.quarter_2} onChange={(v) => set('quarter_2', v)} testid="ea-q2" />
                </Row>
                <Row>
                  <Field label="Date 1" value={draft.date_1} onChange={(v) => set('date_1', v)} testid="ea-date1" />
                  <Field label="Date 2" value={draft.date_2} onChange={(v) => set('date_2', v)} testid="ea-date2" />
                </Row>
              </Section>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex flex-wrap items-center justify-between gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50">
          <div className="text-[11px] text-gray-500">
            <span className="inline-flex items-center gap-1"><CheckCircle size={12} weight="fill" className="text-emerald-500" /> Ready to send — no DocuSign template required</span>
          </div>
          <div className="flex items-center gap-2 ml-auto">
            <Button
              variant="outline"
              onClick={handlePreview}
              disabled={!draft || sending || previewing}
              data-testid="prepare-ea-preview"
            >
              <FileText size={14} className="mr-1.5" /> {previewing ? 'Rendering…' : 'Preview PDF'}
            </Button>
            <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={sending} data-testid="prepare-ea-cancel">Cancel</Button>
            <Button
              onClick={handleSend}
              disabled={!draft || sending}
              className="bg-emerald-700 hover:bg-emerald-800 text-white"
              data-testid="prepare-ea-send"
            >
              <PaperPlaneTilt size={14} className="mr-1.5" />
              {sending ? 'Sending…' : 'Send via DocuSign'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
  // Portal outside the Sheet DOM so button clicks / focus can't propagate
  // to Radix's outside-click detector and close the drawer underneath.
  return createPortal(modalNode, document.body);
}

function Section({ title, hint, children }) {
  return (
    <section className="space-y-3">
      <div className="flex items-baseline gap-2 border-b border-gray-200 pb-1">
        <h3 className="text-[11px] font-bold uppercase tracking-[0.22em] text-gray-700">{title}</h3>
        {hint && <span className="text-[11px] text-gray-500 italic">{hint}</span>}
      </div>
      <div className="space-y-3">{children}</div>
    </section>
  );
}

function Row({ children }) {
  return <div className="flex flex-col sm:flex-row gap-3">{children}</div>;
}

function Field({ label, value, onChange, placeholder, testid }) {
  return (
    <div className="flex-1 min-w-0">
      <Label className="text-[10px] font-bold uppercase tracking-[0.18em] text-gray-500">{label}</Label>
      <Input
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="mt-1 text-sm"
        data-testid={testid}
      />
    </div>
  );
}

/**
 * Amber warning strip that lets an institution admin paste the DocuSign
 * template ID for this student's college *without leaving the modal*.
 * Hits `PUT /admin/colleges/{id}` — requires admin scope. Institution-only
 * users see the raw warning without the save form.
 */
function InlineTemplateFix({ student, onSaved }) {
  const [templateId, setTemplateId] = useState('');
  const [saving, setSaving] = useState(false);
  const collegeId = student?.assigned_college || student?.preferred_college_id;

  const handleSave = async () => {
    if (!collegeId || !templateId.trim()) return;
    setSaving(true);
    try {
      await axios.put(`${API}/admin/colleges/${collegeId}`, {
        docusign_ea_template_id: templateId.trim(),
      });
      toast.success('Template ID saved. Re-loading draft…');
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed — you may need admin permissions.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex-1 min-w-0" data-testid="ea-inline-template-fix">
      <div className="flex items-center gap-2 text-[11px] text-amber-700 mb-1">
        <Warning size={12} weight="fill" />
        <span>No DocuSign EA template on this college yet — paste it below (admin only):</span>
      </div>
      <div className="flex items-center gap-2">
        <Input
          value={templateId}
          onChange={(e) => setTemplateId(e.target.value)}
          placeholder="be6e49a7-4847-473f-9eb5-897d0ae3cbdb"
          className="text-xs font-mono h-8 max-w-md"
          data-testid="ea-inline-template-id"
        />
        <Button size="sm" variant="outline" disabled={!templateId.trim() || saving} onClick={handleSave} data-testid="ea-inline-template-save">
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </div>
    </div>
  );
}


/**
 * ComplianceBanner
 * ----------------
 * Red/yellow/green readiness banner surfaced above every field so staff
 * see the AI compliance verdict BEFORE they scroll. When red, the "Send
 * via DocuSign" button downstream requires a written override reason
 * (enforced server-side too — see /ea/send).
 */
function ComplianceBanner({ compliance }) {
  if (!compliance) return null;
  const overall = (compliance.overall || 'yellow').toLowerCase();
  const issues = compliance.issues || [];
  const extracted = compliance.extracted_values || {};
  const checks = compliance.checks || {};
  // Detect a stale-cache situation: the AI cache exists (that's why the
  // green "AI-detected fields" banner is showing) but the compliance
  // section is empty because the cache pre-dates the audit feature.
  // Show a friendlier "please re-run" message instead of the generic
  // yellow "minor issues" text which is misleading.
  const staleCache = overall === 'yellow' && issues.length === 0 && Object.keys(checks).length === 0 && Object.keys(extracted).length === 0;
  const palette = {
    green:  { bg: 'bg-emerald-50', border: 'border-emerald-300', text: 'text-emerald-900', accent: 'text-emerald-700', Icon: ShieldCheck,   heading: 'All compliance checks passed' },
    yellow: { bg: 'bg-amber-50',   border: 'border-amber-300',   text: 'text-amber-900',   accent: 'text-amber-700',   Icon: ShieldWarning, heading: staleCache ? 'Compliance audit not run yet — click "Re-run AI" above to audit' : 'Minor issues — staff review recommended' },
    red:    { bg: 'bg-red-50',     border: 'border-red-300',     text: 'text-red-900',     accent: 'text-red-700',     Icon: Shield,        heading: 'Critical issues — override required to send' },
  }[overall] || { bg: 'bg-gray-50', border: 'border-gray-200', text: 'text-gray-900', accent: 'text-gray-600', Icon: ShieldWarning, heading: 'Compliance audit unavailable' };
  const { Icon } = palette;

  return (
    <div className={`rounded-md border ${palette.border} ${palette.bg} px-3 py-2.5 text-[12px] ${palette.text}`} data-testid="ea-compliance-banner">
      <div className="flex items-start gap-2">
        <Icon size={16} weight="fill" className={`mt-0.5 shrink-0 ${palette.accent}`} />
        <div className="min-w-0 flex-1">
          <p className="font-semibold">{palette.heading}</p>
          {compliance.summary && !staleCache && <p className={`mt-0.5 ${palette.accent} italic`}>{compliance.summary}</p>}
          {staleCache && (
            <p className={`mt-0.5 ${palette.accent} italic`}>
              This student's AI extraction was cached before the compliance audit feature launched. Re-run the AI to get a fresh red/yellow/green verdict + extracted-values panel.
            </p>
          )}
          {issues.length > 0 && (
            <ul className="mt-2 space-y-1">
              {issues.map((it, i) => (
                <li key={i} className="flex items-start gap-2">
                  <span className={`inline-block mt-0.5 text-[9px] uppercase font-bold tracking-wider px-1.5 py-0.5 rounded ${
                    it.severity === 'critical' ? 'bg-red-200 text-red-900' :
                    it.severity === 'warning' ? 'bg-amber-200 text-amber-900' :
                    'bg-gray-200 text-gray-700'
                  }`}>{it.severity || 'info'}</span>
                  <span className="min-w-0">
                    {it.message}
                    {it.source_doc && <span className={`ml-1 text-[10px] ${palette.accent}`}>({it.source_doc})</span>}
                  </span>
                </li>
              ))}
            </ul>
          )}
          {Object.keys(extracted).length > 0 && (
            <details className="mt-2">
              <summary className={`cursor-pointer text-[11px] font-semibold ${palette.accent}`}>Extracted values (from source docs)</summary>
              <div className="mt-1.5 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 text-[11px]">
                {Object.entries(extracted).map(([key, val]) => (
                  <div key={key} className="flex items-baseline gap-1.5">
                    <span className={`shrink-0 uppercase tracking-wider text-[9px] font-bold ${palette.accent}`}>{key.replace(/_/g, ' ')}:</span>
                    <span className="min-w-0 break-all">
                      {val?.value || '—'}
                      {val?.source && <span className={`ml-1 text-[10px] ${palette.accent}`}>({val.source})</span>}
                    </span>
                  </div>
                ))}
              </div>
            </details>
          )}
        </div>
      </div>
    </div>
  );
}
